import { useState, useEffect, useCallback } from 'react';
import { format as formatDateFns } from 'date-fns';
import api from '../services/api';

export const useDashboardData = (user, refreshKey) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);

  const calculateAverageRate = (day) => {
    if (!day || !day.hours || day.hours <= 0) return 0;
    const totalEarnings =
      (day.hourlyTotal || 0) +
      (day.commission || 0) +
      (day.tips || 0) +
      (day.charmBonus || 0);
    return totalEarnings / day.hours;
  };

  const fetchCombinedDashboardData = useCallback(async () => {
    if (!user || !user.id) {
      setLoadingDashboard(false);
      return;
    }

    setLoadingDashboard(true);

    try {
      const today = new Date();
      const todayFormatted = formatDateFns(today, 'yyyy-MM-dd');

      const dayOfWeek = today.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(today);
      monday.setDate(today.getDate() + diffToMonday);
      const mondayFormatted = formatDateFns(monday, 'yyyy-MM-dd');

      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const sundayFormatted = formatDateFns(sunday, 'yyyy-MM-dd');

      const [dashboardInfoRes, dailySalaryDetailsRes, weeklySalaryTotalRes] =
        await Promise.all([
          api.get(`/dashboard/me`),
          api.get(
            `/salary/${user.id}/details?start_date=${todayFormatted}&end_date=${todayFormatted}`
          ),
          api.get(
            `/salary/${user.id}?start_date=${mondayFormatted}&end_date=${sundayFormatted}`
          ),
        ]);

      const dashboardInfo = dashboardInfoRes.data;

      const dailySalaryDataArray = dailySalaryDetailsRes.data?.dailyData;
      const todaySalaryData =
        dailySalaryDataArray && dailySalaryDataArray.length > 0
          ? dailySalaryDataArray.find((d) => d.date === todayFormatted) || {}
          : {};

      const weeklyTotalSalary = weeklySalaryTotalRes.data?.total;

      // ✅ SALES:
      // Preferir o subtotal que vem do /salary/<id>/details (salesSubtotal = soma de itens, sem tip)
      // Fallback: pega do /dashboard/me e subtrai tip (caso o /dashboard/me venha com tip embutida)
      const salesSubtotalFromDetails =
        todaySalaryData.salesSubtotal !== undefined && todaySalaryData.salesSubtotal !== null
          ? parseFloat(todaySalaryData.salesSubtotal) || 0
          : null;

      const totalSalesFromDashboardMe =
        dashboardInfo.total_sales_day !== undefined && dashboardInfo.total_sales_day !== null
          ? parseFloat(dashboardInfo.total_sales_day) || 0
          : 0;

      const tipsForDay = parseFloat(todaySalaryData.tips) || 0;

      const correctedTotalSales =
        salesSubtotalFromDetails !== null
          ? salesSubtotalFromDetails
          : totalSalesFromDashboardMe - tipsForDay;

      const averageRateToday = calculateAverageRate(todaySalaryData);

      setDashboardData({
        name: user.name,

        // ✅ usa o que veio do backend (mais consistente), com fallback local
        date: dashboardInfo.date_string || formatDateFns(today, 'MM/dd/yyyy'),
        resort: dashboardInfo.resort,
        clock: dashboardInfo.clock,
        schedule: dashboardInfo.schedule,
        group: dashboardInfo.group || 'N/A',

        // ✅ compatibilidade com EmployeeDashboard.jsx:
        // ele lê total_sales_day e charms
        total_sales_day: correctedTotalSales.toFixed(2),
        charms:
          todaySalaryData.charmsQty !== undefined && todaySalaryData.charmsQty !== null
            ? String(parseInt(todaySalaryData.charmsQty, 10) || 0)
            : '0',

        // ✅ se em algum lugar do sistema você usa total_sales, mantém também
        total_sales: correctedTotalSales.toFixed(2),

        hourly_total:
          todaySalaryData.hourlyTotal !== undefined
            ? parseFloat(todaySalaryData.hourlyTotal).toFixed(2)
            : '0.00',
        commission:
          todaySalaryData.commission !== undefined
            ? parseFloat(todaySalaryData.commission).toFixed(2)
            : '0.00',
        tip:
          todaySalaryData.tips !== undefined
            ? parseFloat(todaySalaryData.tips).toFixed(2)
            : '0.00',
        charm_bonus:
          todaySalaryData.charmBonus !== undefined
            ? parseFloat(todaySalaryData.charmBonus).toFixed(2)
            : '0.00',
        total_day:
          todaySalaryData.total !== undefined
            ? parseFloat(todaySalaryData.total).toFixed(2)
            : '0.00',
        total_week:
          weeklyTotalSalary !== undefined && weeklyTotalSalary !== null
            ? parseFloat(weeklyTotalSalary).toFixed(2)
            : '0.00',

        average_rate_today: averageRateToday.toFixed(2),

        // ✅ % da meta do dia — vem pronta do backend, sem valor de venda junto,
        // por isso continua visível durante o turno (não entra no blur)
        daily_goal_percent:
          dashboardInfo.daily_goal_percent !== undefined && dashboardInfo.daily_goal_percent !== null
            ? Number(dashboardInfo.daily_goal_percent)
            : null,

        // ✅ emoji
        avatar_emoji: dashboardInfo.avatar_emoji || '🙂',
      });
    } catch (err) {
      console.error('Error fetching combined dashboard data:', err);
      setDashboardData(null);
    } finally {
      setLoadingDashboard(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCombinedDashboardData();
  }, [fetchCombinedDashboardData, refreshKey]);

  return { dashboardData, loadingDashboard, refetch: fetchCombinedDashboardData };
};
